import { Customer, PaginatedResponse, ApiResponse, CustomerType } from '../types';
import { apiClient } from './apiClient';

export interface CreateCustomerRequest {
  name: string;
  phone?: string;
  address?: string;
  customer_type?: CustomerType;
  is_active?: boolean;
}

export interface UpdateCustomerRequest extends Partial<CreateCustomerRequest> {
  id: number;
}

export interface CustomerFilters {
  search?: string;
  sortBy?: 'name' | 'created_at' | 'balance_amount';
  sortOrder?: 'asc' | 'desc';
  balanceStatus?: 'all' | 'paid' | 'pending';
  customerType?: 'all' | 'retail' | 'home' | 'wholesale' | 'business' | 'regular' | 'vip';
  activeStatus?: 'all' | 'active' | 'inactive';
}

export interface PaymentRequest {
  amount: number;
  notes?: string;
}

class CustomerService {
  private baseUrl = '/customers';

  async getCustomers(
    filters: CustomerFilters = {},
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<Customer>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => 
            value !== undefined && value !== '' && value !== 'all'
          )
        ),
      });

      const response = await apiClient.get<any>(
        `${this.baseUrl}?${params}`
      );

      if (response.success) {
        // The backend returns the pagination data directly, not nested in a data property
        return {
          data: response.data as Customer[],
          total: response.total || 0,
          last_page: response.last_page || 1,
          current_page: response.current_page || 1,
          per_page: response.per_page || 10,
          from: ((response.current_page || 1) - 1) * (response.per_page || 10) + 1,
          to: Math.min((response.current_page || 1) * (response.per_page || 10), response.total || 0),
        };
      }

      throw new Error(response.message || 'Failed to fetch customers');
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      // Don't throw the error if it's an auth issue - let the auth system handle it
      if (error.response?.status === 401) {
        throw error; // Re-throw to let apiClient handle auth
      }
      throw error;
    }
  }

  async getCustomer(id: number): Promise<Customer> {
    try {
      const response = await apiClient.get<ApiResponse<Customer>>(`${this.baseUrl}/${id}`);
      
      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch customer');
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  }

  async createCustomer(customerData: CreateCustomerRequest): Promise<Customer> {
    try {
      const response = await apiClient.post<ApiResponse<Customer>>(
        this.baseUrl, 
        customerData
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to create customer');
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  async updateCustomer(customerData: UpdateCustomerRequest): Promise<Customer> {
    try {
      const { id, ...updateData } = customerData;
      const response = await apiClient.put<ApiResponse<Customer>>(
        `${this.baseUrl}/${id}`, 
        updateData
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to update customer');
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  async deleteCustomer(id: number): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  }

  async getCustomerSales(id: number): Promise<any[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(`${this.baseUrl}/${id}/sales`);
      
      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch customer sales');
    } catch (error) {
      console.error('Error fetching customer sales:', error);
      throw error;
    }
  }

  async searchCustomers(query: string): Promise<Customer[]> {
    try {
      const response = await apiClient.get<ApiResponse<Customer[]>>(
        `${this.baseUrl}/search?q=${encodeURIComponent(query)}`
      );
      
      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to search customers');
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  }

  // Dues Management Methods
  async addPayment(id: number, paymentData: PaymentRequest): Promise<Customer> {
    try {
      const response = await apiClient.post<ApiResponse<Customer>>(
        `${this.baseUrl}/${id}/payment`, 
        paymentData
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to add payment');
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  }

  async clearDues(id: number): Promise<Customer> {
    try {
      const response = await apiClient.post<ApiResponse<Customer>>(
        `${this.baseUrl}/${id}/clear-dues`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to clear dues');
    } catch (error) {
      console.error('Error clearing dues:', error);
      throw error;
    }
  }

  async getPaymentHistory(id: number): Promise<any[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        `${this.baseUrl}/${id}/payments`
      );
      
      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch payment history');
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  // Bulk Operations
  async bulkDelete(ids: number[]): Promise<{ deleted_count: number }> {
    try {
      const response = await apiClient.post<ApiResponse<{ deleted_count: number }>>(
        `${this.baseUrl}/bulk-delete`,
        { ids }
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to delete customers');
    } catch (error) {
      console.error('Error deleting customers:', error);
      throw error;
    }
  }

  async bulkUpdateStatus(ids: number[], isActive: boolean): Promise<{ updated_count: number }> {
    try {
      const response = await apiClient.post<ApiResponse<{ updated_count: number }>>(
        `${this.baseUrl}/bulk-status`,
        { ids, is_active: isActive }
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to update customer status');
    } catch (error) {
      console.error('Error updating customer status:', error);
      throw error;
    }
  }
}

export const customerService = new CustomerService();